import React, {CSSProperties, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {CoreDoms, CoreEvent, Viewers} from '@taopix/taopix-core';
import {ComputedPageItem} from '@taopix/taopix-core/dist/types/types';
import {Button, Theme} from '@taopix/taopix-design-system';
import classNames from 'classnames';
import {DialogStatus} from '../../../../Enums';
import {UserFlowStep} from '../../../../Facade/UserFlow/BaseUserFlow';
import {UserFlowType} from '../../../../Facade/UserFlow/UserFlowFacade';
import useSettings from '../../../../Hooks/useSettings';
import useViewMode from '../../../../Hooks/useViewMode';
import {LayoutSelector, PageListSelector} from '../../../../Selectors';
import {useTaopix} from '../../../Context/TaopixContext';
import {useUserFlowState} from '../../../Context/UserFlow/UserFlowContext';
import {UserInterfaceActions} from '../../../Context/UserInterface/UserInterfaceActionTypes';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import {useExperience} from '../../../Experience/ExperienceProvider';
import GreetingCardView from '../../../Views/Pages/GreetingCardView';
import LayoutLockSwitch from '../../../LayoutLockSwitch/LayoutLockSwitch';
import WorkareaToolbar from '../../Photobook/Project/WorkareaToolbar';
import {useTranslation} from 'react-i18next';

export type ProjectProps = {
  selectedItem: ComputedPageItem;
};

export type greetingCard3dViewType = 'front' | 'inside-left' | 'inside-right' | 'back';

const Project = ({selectedItem}: ProjectProps): JSX.Element => {
  const app = useTaopix();
  const settings = useSettings();
  const ui = useUiState();
  const userFlow = useUserFlowState();
  const pageNumberList = useSelector(PageListSelector);
  const {experience} = useExperience();
  const [viewMode] = useViewMode();
  const {t} = useTranslation(['*']);

  const [viewOffsetState, setViewOffsetState] = useState<number>(ui.state.viewOffset);
  const [current3DView, setCurrentView] = useState<greetingCard3dViewType>('front');
  const [playIntroAnimation, setPlayIntroAnimation] = useState(false);

  // get some props from the core
  const stateManager = app.core.get('stateManager');

  // get the layout from the store
  const layout = useSelector(LayoutSelector);

  // get the pageCount for pagination
  const [, setSnapStatus] = useState(true);

  // checks for showing save prompt returns true if no known dialogs are open
  // To Review - this will not catch all dialogs, not sure the way to do that,
  // all portal ids end in -portal check that none are in dom?
  const checkUiDialogs = () => {
    return (
      !ui.state.calendarCloseDialog &&
      !ui.state.calendarEditPictureDialog &&
      ui.state.dialogs.coverSpineChangeNotification === DialogStatus.Closed &&
      ui.state.dialogs.pageCountChangeNotification === DialogStatus.Closed &&
      ui.state.dialogs.projectLayoutChangeNotification === DialogStatus.Closed &&
      !ui.state.languageDialogOpen &&
      !userFlow.getCurrentStep(UserFlowType.Save).includes(UserFlowStep.ProjectName) &&
      !userFlow.isUserFlowActive(UserFlowType.Save) &&
      !userFlow.isUserFlowActive(UserFlowType.Share) &&
      !userFlow.isUserFlowActive(UserFlowType.Upgrade)
    );
  };

  const showSavePrompt =
    !ui.state.busy &&
    ui.state.saveCount < 1 &&
    checkUiDialogs() &&
    userFlow.getCurrentStep(UserFlowType.UserFlow) ===
      `${UserFlowType.UserFlow}.${UserFlowStep.None}`;

  // subscribe to global page events
  useEffect(() => {
    const tokens: string[] = [];

    tokens.push(
      app.core.subscribe(CoreEvent.UPDATE_ITEM, (msg: string, data: any) => {
        stateManager.dispatch(data);
      })
    );

    tokens.push(
      app.core.subscribe(CoreEvent.CORE_BUSY, (msg: string, data: any) => {
        ui.dispatch(UserInterfaceActions.setIsBusy(data));
      })
    );

    tokens.push(
      app.core.subscribe(CoreEvent.SNAP_STATUS_TOGGLE, (msg: string, data: any): void => {
        setSnapStatus(data);
      })
    );

    tokens.push(
      app.core.subscribe(CoreEvent.ZOOM_CHANGED, (msg: string, level: number) => {
        if (ui.state.zoomLevel !== level) {
          ui.dispatch(UserInterfaceActions.setZoomLevel(Number(level.toFixed(2))));
        }
      })
    );

    tokens.push(
      app.core.subscribe(CoreEvent.SET_SELECTED_PAGENUMBER, (msg: string, data: any) => {
        ui.dispatch(UserInterfaceActions.setPageNumber(data));
      })
    );

    tokens.push(
      app.core.subscribe(CoreEvent.SET_SELECTED_SIDE, (msg: string, data: any) => {
        ui.dispatch(UserInterfaceActions.setSelectedSide(data));
      })
    );

    tokens.push(
      app.core.subscribe(CoreEvent.SWAP_ACTIVE, (msg: string, data: any) => {
        ui.dispatch(UserInterfaceActions.setSwapActive(data));
      })
    );

    return () => {
      tokens.forEach((token, index) => {
        app.core.unsubscribe(token);
        delete tokens[index];
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem?.ref, ui.state.zoomLevel, layout]);

  useEffect(() => {
    const tokens: string[] = [];

    if (settings.state.authentication.userRegistered) {
      tokens.push(
        app.core.subscribe(CoreEvent.INACTIVITY_DETECTED, (msg: string, data: any) => {
          // User is registered
          // app is not busy
          // Inactivity dialog not already showing
          // TODO: Test if SSO
          // TODO: Test that we are not in the share preview window in MediaAlbumWeb
          if (
            app.state.loaded &&
            !ui.state.busy &&
            !userFlow.getCurrentStep(UserFlowType.Auth).includes(UserFlowStep.Logout)
          ) {
            userFlow.setCurrentStep(`${UserFlowStep.Auth}.${UserFlowStep.Logout}`);
          }
        })
      );
    }

    tokens.push(
      app.core.subscribe(CoreEvent.SAVE_KEYBOARD, () => {
        userFlow.activateUserFlow(UserFlowType.Save);
      })
    );

    return () => {
      tokens.forEach((token, index) => {
        app.core.unsubscribe(token);
        delete tokens[index];
      });
    };
  }, [settings.state.authentication.userRegistered]);

  useEffect(() => {
    const tokens: string[] = [];

    tokens.push(
      app.core.subscribe(CoreEvent.SAVE_PROMPT, (msg: string, data: any) => {
        if (showSavePrompt) {
          ui.dispatch(UserInterfaceActions.setSavePrompt(true));
        }
      })
    );

    return () => {
      tokens.forEach((token, index) => {
        app.core.unsubscribe(token);
        delete tokens[index];
      });
    };
  }, [showSavePrompt]);

  const containerRef = React.useRef<HTMLDivElement>(null);

  const workAreaClassNames = classNames(
    'flex',
    'flex-col',
    'flex-1',
    'relative',
    'min-h-0',
    'items-center',
    'transition-[transform]',
    // ********* We should probably update this to match the way the work area offset works in photobooks now *********
    '[.focus-mode-on_&]:translate-y-[calc((0-var(--viewOffset))*1rem)]'
  );

  const projectViewerClassNames = classNames(
    'flex',
    'relative',
    'w-full',
    'h-full',
    'transition-[all] duration-[0.3s] ease-out',
    '!overflow-visible', // Override the Core's scroll behaviour to prevent the card being clipped by the container
    'sm:focusMode:!overflow-y-auto', // Add scrolling back on for focus mode
    playIntroAnimation && 'tpx-gc-project-viewer--animate',
    'tpx-gc-project-viewer--show-' + current3DView
  );

  const tpxCoreAttrs = {
    [CoreDoms.ProjectViewerAttr]: Viewers.Primary,
    [CoreDoms.InsetWidthAttr]: '80',
    [CoreDoms.InsetHeightAttr]: '40',
    [CoreDoms.ResizeThresholdAttr]: '20',
    [CoreDoms.ListDirectionAttr]: 'column',
  };

  useEffect(() => {
    // Populate the view offset state for the different view modes
    const viewOffset = 70;
    setViewOffsetState(ui.state.isSmallScreen ? viewOffset : 0);
  }, [viewMode, ui.state.isSmallScreen]);

  const workareaVars = {
    '--viewOffset': viewOffsetState,
  } as CSSProperties;

  const projectViewerProperties = {
    '--card-scale': 1,
  };

  useEffect(() => {
    const startAnimationTimeout = setTimeout(() => {
      setPlayIntroAnimation(true);
    }, 2000);

    return () => {
      clearTimeout(startAnimationTimeout);
    };
  }, []);

  return (
    <Theme
      name={'tpx-theme-workarea'}
      tipLabel={'Workarea'}
      isRootTheme
      allowBorder={false}
      id="workarea"
      data-track-position
      className={workAreaClassNames}
      style={workareaVars}
      allowCorners={false}
    >
      <WorkareaToolbar layout={layout} />

      <div
        {...tpxCoreAttrs}
        className={projectViewerClassNames}
        ref={containerRef}
        id={'projectViewer'}
        style={projectViewerProperties as CSSProperties}
      >
        <GreetingCardView
          layout={layout}
          setCurrentView={setCurrentView}
          current3DView={current3DView}
        />
      </div>
      <div className="flex space-x-[1px] w-full justify-center p-lg">
        <Button
          label={t('*:str_LabelFrontCover2')}
          size="small"
          buttonStyle="tertiary"
          aria-pressed={current3DView === 'front'}
          tabPosition={'left'}
          onClick={() => {
            setCurrentView('front');
            app.core.get('abilities').setPage(pageNumberList[0]).then();
          }}
        />
        {pageNumberList.length > 2 && (
          // If there are more than two pages in this product, show the 'Inside' button
          <Button
            label={t('*:str_LabelInside', {defaultValue: 'Inside'})}
            size="small"
            buttonStyle="tertiary"
            aria-pressed={['inside-left', 'inside-right'].includes(current3DView)}
            onClick={() => {
              setCurrentView('inside-right');
              app.core.get('abilities').setPage(pageNumberList[2]).then();
            }}
            tabPosition={'middle'}
          />
        )}
        <Button
          label={t('*:str_LabelBackCover2')}
          size="small"
          buttonStyle="tertiary"
          aria-pressed={current3DView === 'back'}
          onClick={() => {
            setCurrentView('back');
            // The 'Back' button shows the last page
            app.core
              .get('abilities')
              .setPage(pageNumberList[pageNumberList.length - 1])
              .then();
          }}
          tabPosition={'right'}
        />

        {experience.EDITOR.pageEditing.allowUsersToToggleLocking && !ui.state.isSmallScreen && (
          // Show the large screen LayoutLockSwitch
          <div className={classNames('absolute', 'bottom-sm', 'right-sm', 'mt-sm', 'z-10')}>
            <LayoutLockSwitch />
          </div>
        )}
      </div>
    </Theme>
  );
};

export default Project;
